import { Capacitor } from '@capacitor/core';
import { Keyboard, type KeyboardInfo } from '@capacitor/keyboard';
import type { PluginListenerHandle } from '@capacitor/core';
import { useEffect } from 'react';

const CSS_VAR = '--keyboard-inset-height';
const KEYBOARD_OPEN_ATTR = 'data-keyboard-open';

/**
 * Exposes native keyboard height as `--keyboard-inset-height` on `<html>`.
 * Used with `Keyboard.resize: 'none'` so fixed chrome (tab bar) stays put while
 * scroll containers and drawers can pad/shift above the keyboard.
 */
export function useCapacitorKeyboardInset(): void {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const root = document.documentElement;

    const setInset = (px: number) => {
      const value = Math.max(0, Math.round(px));
      if (value === 0) {
        root.style.removeProperty(CSS_VAR);
        root.removeAttribute(KEYBOARD_OPEN_ATTR);
      } else {
        root.style.setProperty(CSS_VAR, `${value}px`);
        root.setAttribute(KEYBOARD_OPEN_ATTR, '');
      }
    };

    const handles: PluginListenerHandle[] = [];
    let cancelled = false;

    const register = async () => {
      try {
        const willShow = await Keyboard.addListener(
          'keyboardWillShow',
          (info: KeyboardInfo) => setInset(info.keyboardHeight),
        );
        const didShow = await Keyboard.addListener(
          'keyboardDidShow',
          (info: KeyboardInfo) => setInset(info.keyboardHeight),
        );
        const willHide = await Keyboard.addListener('keyboardWillHide', () =>
          setInset(0),
        );
        const didHide = await Keyboard.addListener('keyboardDidHide', () =>
          setInset(0),
        );

        if (cancelled) {
          await Promise.all(
            [willShow, didShow, willHide, didHide].map((h) =>
              h.remove().catch(() => undefined),
            ),
          );
          return;
        }
        handles.push(willShow, didShow, willHide, didHide);
      } catch (error) {
        console.warn(
          '[useCapacitorKeyboardInset] Failed to register keyboard listeners:',
          error,
        );
      }
    };

    register();

    return () => {
      cancelled = true;
      setInset(0);
      for (const h of handles) {
        h.remove().catch(() => undefined);
      }
    };
  }, []);
}
